import { t, type PlainMessageKey } from '@/lib/i18n';
import styles from './DistributionBar.module.css';

export interface Distribution {
  birdiesOrBetter: number;
  pars: number;
  bogeys: number;
  doubleOrWorse: number;
}

const SEGMENTS: ReadonlyArray<{
  key: keyof Distribution;
  label: PlainMessageKey;
  /** Vem de um CSS module, que o TS tipa como possivelmente ausente. */
  className: string | undefined;
}> = [
  { key: 'birdiesOrBetter', label: 'dist.birdies', className: styles.birdie },
  { key: 'pars', label: 'dist.pars', className: styles.par },
  { key: 'bogeys', label: 'dist.bogeys', className: styles.bogey },
  { key: 'doubleOrWorse', label: 'dist.doubles', className: styles.double },
];

/**
 * A 100%-width breakdown of a round's holes (canvas 1d, 1l).
 *
 * The canvas argues for this over an average: "consistência diz mais que média". Two rounds
 * of 86 can be six birdies and six doubles, or eighteen bogeys — and those are different
 * games.
 */
export function DistributionBar({
  distribution,
  showLegend = true,
}: {
  distribution: Distribution;
  showLegend?: boolean;
}) {
  const total =
    distribution.birdiesOrBetter +
    distribution.pars +
    distribution.bogeys +
    distribution.doubleOrWorse;

  if (total === 0) return null;

  return (
    <div>
      <div className={styles.bar}>
        {SEGMENTS.map(({ key, label, className }) => {
          const value = distribution[key];
          if (value === 0) return null;
          return (
            <div
              key={key}
              className={className}
              style={{ width: `${(value / total) * 100}%` }}
              title={`${t(label)}: ${value}`}
            />
          );
        })}
      </div>

      {showLegend && (
        <div className={styles.legend}>
          {SEGMENTS.map(({ key, label, className }) => (
            <span key={key} className={styles.legendItem}>
              <span className={`${styles.dot} ${className}`} aria-hidden="true" />
              {t(label)} {distribution[key]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
